import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface DbExtra {
  id: string;
  product_id: string;
  name_de: string;
  name_en: string;
  price: number;
  sort_order: number;
}

export interface DbProduct {
  id: string;
  category_id: string;
  name_de: string;
  name_en: string;
  description_de: string;
  description_en: string;
  price: number;
  image_url: string;
  active: boolean;
  popular: boolean;
  badge_de: string | null;
  badge_en: string | null;
  sort_order: number;
  extras: DbExtra[];
}

export interface DbCategory {
  id: string;
  name_de: string;
  name_en: string;
  icon: string;
  sort_order: number;
  active: boolean;
}

interface UseMenuDataResult {
  categories: DbCategory[];
  products: DbProduct[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useMenuData = (): UseMenuDataResult => {
  const [categories, setCategories] = useState<DbCategory[]>([]);
  const [products, setProducts] = useState<DbProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = () => setTick((t) => t + 1);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const [{ data: cats, error: catErr }, { data: prods, error: prodErr }, { data: exts, error: extErr }] =
        await Promise.all([
          supabase.from('categories').select('*').eq('active', true).order('sort_order'),
          supabase.from('products').select('*').eq('active', true).order('sort_order'),
          supabase.from('extras').select('*').order('sort_order'),
        ]);

      if (cancelled) return;

      if (catErr || prodErr || extErr) {
        setError('Menü yüklenirken hata oluştu.');
        setLoading(false);
        return;
      }

      const extrasMap: Record<string, DbExtra[]> = {};
      (exts ?? []).forEach((ext) => {
        if (!extrasMap[ext.product_id]) extrasMap[ext.product_id] = [];
        extrasMap[ext.product_id].push({
          id: ext.id,
          product_id: ext.product_id,
          name_de: ext.name_de,
          name_en: ext.name_en,
          price: Number(ext.price),
          sort_order: ext.sort_order,
        });
      });

      const mappedProducts: DbProduct[] = (prods ?? []).map((p) => ({
        id: p.id,
        category_id: p.category_id,
        name_de: p.name_de,
        name_en: p.name_en,
        description_de: p.description_de ?? '',
        description_en: p.description_en ?? '',
        price: Number(p.price),
        image_url: p.image_url ?? '',
        active: p.active,
        popular: p.popular,
        badge_de: p.badge_de ?? null,
        badge_en: p.badge_en ?? null,
        sort_order: p.sort_order,
        extras: extrasMap[p.id] ?? [],
      }));

      setCategories(cats ?? []);
      setProducts(mappedProducts);
      setLoading(false);
    };

    fetchData();
    return () => { cancelled = true; };
  }, [tick]);

  return { categories, products, loading, error, refetch };
};
